import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, ArrowLeft, Loader2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { casesAPI, documentsAPI } from '../utils/api';

// Section components
import CaseOverviewSection from '../components/case-detail/CaseOverviewSection';
import PassengersSection from '../components/case-detail/PassengersSection';
import FlightSegmentsSection from '../components/case-detail/FlightSegmentsSection';
import DocumentsSection from '../components/case-detail/DocumentsSection';
import MessagesSection from '../components/case-detail/MessagesSection';
import ExpensesSection from '../components/case-detail/ExpensesSection';
import InboundEmailSection from '../components/case-detail/InboundEmailSection';

// Legal workflow components
import { CaseProgress } from '../components/client/CaseProgress';
import { ClientTimeline } from '../components/client/ClientTimeline';

const getStatusVariant = (state) => {
  const variants = {
    intake: 'default',
    under_review: 'secondary',
    documentation: 'outline',
    compensation: 'success',
    closed: 'secondary',
  };
  return variants[state] || 'secondary';
};

const getStatusLabel = (state) => {
  const labels = {
    intake: 'Intake',
    under_review: 'Under Review',
    documentation: 'Documentation',
    compensation: 'Compensation',
    closed: 'Closed',
  };
  return labels[state] || state;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const CaseDetailPage = ({ caseId }) => {
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [inboundEmails, setInboundEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchCaseDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await casesAPI.getCaseById(caseId);

      if (result.status === 'success') {
        setCaseData(result.case);
      } else {
        setError('Failed to load case details');
      }
    } catch (err) {
      console.error('Error fetching case:', err);
      if (err.response?.status === 404) {
        setError('Case not found');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view this case');
      } else {
        setError('Failed to load case details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  const fetchInboundEmails = useCallback(async () => {
    try {
      const result = await documentsAPI.getInboundEmails(caseId);
      if (result.status === 'success') {
        setInboundEmails(result.emails || []);
      }
    } catch (err) {
      console.error('Error fetching inbound emails:', err);
      // Non-critical error, don't set global error
    }
  }, [caseId]);

  useEffect(() => {
    fetchCaseDetail();
    fetchInboundEmails();
  }, [fetchCaseDetail, fetchInboundEmails]);

  const handleBack = () => navigate({ to: '/client' });
  const refreshCase = () => fetchCaseDetail();

  const handleContinueIntake = () => {
    navigate({ to: '/client/new-claim', search: { resumeCase: String(caseData?.id) } });
  };

  const handleDeleteConfirm = async () => {
    if (!caseData) return;
    try {
      setDeleting(true);
      const result = await casesAPI.deleteCase(caseData.id);
      if (result.status === 'success') {
        toast.success(result.message || 'Case deleted successfully');
        navigate({ to: '/client' });
      }
    } catch (err) {
      console.error('Error deleting case:', err);
      toast.error('Failed to delete case. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-slate-500">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error || 'Case not found'}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const isDraft = caseData.intake_step !== 'completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Case {caseData.case_number}</h1>
            {isDraft && (
              <Badge variant="warning">Draft</Badge>
            )}
            <Badge variant={getStatusVariant(caseData.state)}>
              {getStatusLabel(caseData.state)}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">
              Created {formatDate(caseData.created_at)}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isDraft && (
                  <DropdownMenuItem onClick={handleContinueIntake}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Continue Intake
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setDeleteModalOpen(true)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Case
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Case Details (2/3) */}
          <div className="lg:col-span-2">
            <Card className="p-6 border-0 shadow-lg bg-white">
              <div className="space-y-6">
                {/* Overview */}
                <CaseOverviewSection caseData={caseData} />

                <Separator />

                {/* Passengers & Flights side by side */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <PassengersSection passengers={caseData.passengers || []} />
                  <div className="md:col-span-2">
                    <FlightSegmentsSection segments={caseData.flight_segments || []} />
                  </div>
                </div>

                <Separator />

                {/* Documents */}
                <DocumentsSection
                  caseId={caseId}
                  documents={caseData.documents || []}
                  onDocumentAdded={refreshCase}
                  isSubmitted={!isDraft}
                />

                <Separator />

                {/* Messages */}
                <MessagesSection caseId={caseId} />

                <Separator />

                {/* Expenses */}
                <ExpensesSection
                  caseId={caseId}
                  expenses={caseData.expenses || []}
                  onExpenseAdded={refreshCase}
                />
              </div>
            </Card>
          </div>

          {/* Right Column - Progress & Timeline (1/3) */}
          <div className="space-y-6">
            {/* Inbound Email Address */}
            <Card className="p-5 border-0 shadow-lg bg-white">
              <InboundEmailSection
                caseData={caseData}
                inboundEmails={inboundEmails}
              />
            </Card>

            {/* Case Progress */}
            {!isDraft && (
              <Card className="p-5 border-0 shadow-lg bg-white">
                <CaseProgress caseId={caseId} />
              </Card>
            )}

            {/* Case Timeline */}
            {!isDraft && (
              <Card className="p-5 border-0 shadow-lg bg-white">
                <ClientTimeline caseId={caseId} />
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Case</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete case{' '}
              <span className="font-semibold">{caseData.case_number}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseDetailPage;
